import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Snackbar,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";

// components
import { TooltipCustom } from "../../ui/tooltip-custom";
import { UNITS } from "../../../constants/site-settings.constants";
import { apiUploadProperty } from "../../../services/api/site-settings-api";

// svg
import { ReactComponent as IconDelete } from "../../../assets/svg/delete.svg";
import { ReactComponent as IconUpload } from "../../../assets/svg/upload-cloud.svg";
import { ReactComponent as IconFileDXF } from "../../../assets/svg/dxf-file-icon.svg";
import { ReactComponent as IconDownload } from "../../../assets/svg/download.svg";
import { ReactComponent as IconCopy } from "../../../assets/svg/copy.svg";

export const StepUpload = ({
  units,
  file,
  deleteFileHandler,
  saveFileHandler,
  uploading,
  setUploading,
  fileId,
  fileName,
}) => {
  const { projectId } = useParams();
  const [error, setError] = useState(null);

  const onDrop = useCallback((files) => {
    if (!files.length) return;
    const file = files[0];
    setUploading(true);
    setError(null);
    (async () => {
      try {
        if (
          file.name.toLowerCase().endsWith(".dxf") ||
          file.name.toLowerCase().endsWith(".dwg")
        ) {
          const formData = new FormData();
          formData.append("file", file, file.name);

          const data = await apiUploadProperty(projectId, formData);
          /* console.log(data, "apiUploadFile"); */

          saveFileHandler(data, file);
        } else {
          setError("Unsupported file type. Please upload .dxf file.");
        }
      } catch (err) {
        /* console.log(err); */
        const message =
          err.response?.data?.message ||
          err.response?.data?.errormsg ||
          "Failed to read CAD file";
        setError(message);
      } finally {
        setUploading(false);
      }
    })();
  }, []);

  const maxBytes = 100 * 1024 * 1024; // 100 MB, or omit for “no limit”

  const onReject = useCallback(() => {
    setError("Unsupported file type. Please upload .dxf file.");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.dxf": [".dxf"],
      /* "application/x-autocad": [".dwg"], */
      "application/octet-stream": [".dxf", /* ".dwg" */],
    },
    onDropAccepted: onDrop,
    onDropRejected: onReject,
    multiple: false,
    /* maxSize: maxBytes, */
  });

  const [openNotification, setOpenNotification] = useState(false);

  const handleCopy = (e) => {
    navigator.clipboard.writeText("property_outline");
    setOpenNotification(true);
  };

  return (
    <Box
      sx={{
        mt: "20px",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {fileId ? (
        <Box
          sx={{
            width: "100%",
            height: "auto",
            background: "#f1f1f8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px",
            gap: "24px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <IconFileDXF width="20px" height="20px" fill="#000" />
            <Box>
              <Typography variant="body2">
                {file ? file.name : fileName ? fileName : "Uploaded file"}
              </Typography>
              <Typography
                variant="body2"
                color="#777"
                mt="4px"
                fontSize={"14px"}
              >
                {file && file.size
                  ? `${(file.size / 1024).toFixed(1)} kB | `
                  : ""}{" "}
                Units: {units ? UNITS[units] : "not found"}
              </Typography>
            </Box>
          </Box>

          <Box>
            <TooltipCustom text={"Delete Uploaded File"} placement="top">
              <IconButton onClick={deleteFileHandler}>
                <IconDelete fill="#000" width={"18px"} height={"18px"} />
              </IconButton>
            </TooltipCustom>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box
            {...getRootProps()}
            sx={{
              p: 4,
              border: "2px dashed #343745",
              borderColor: isDragActive ? "#efa21e" : "#c7c7d6",
              borderRadius: 2,
              textAlign: "center",
              mt: "24px",
              mb: "24px",
              height: "200px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              background: isDragActive
                ? "rgba(239, 162, 30, 0.04)"
                : "none" /* "#eeeef6" */,
            }}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <CircularProgress />
            ) : isDragActive ? (
              <>
                <Typography variant="h6">Drop it!</Typography>
              </>
            ) : (
              <>
                <IconUpload stroke={"#343745"} width="32px" height="32px" />
                <Typography variant="h6" gutterBottom>
                  Drag & drop a DXF {/* / DWG */} file here
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to select file
                </Typography>
              </>
            )}
          </Box>
          <Alert
            variant="outlined"
            severity={"warning"}
            sx={{ marginBottom: "24px" }}
          >
            <strong>Note:</strong> Your CAD file must contain a layer named{" "}
            <Typography
              component={"span"}
              fontSize={"0.875rem"}
              backgroundColor="rgb(236 182 91 / 46%)"
              p="0 4px"
              fontWeight={500}
            >
              {" "}
              property_outline{" "}
            </Typography>{" "}
            <IconButton size="small" sx={{ ml: "4px" }} onClick={handleCopy}>
              <IconCopy stroke="rgb(102, 60, 0)" width="12px" height="12px" />
            </IconButton>{" "}
            holding the site polygon
          </Alert>
          <Snackbar
            message="Copied to clibboard"
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            autoHideDuration={2000}
            onClose={() => setOpenNotification(false)}
            open={openNotification}
          />
        </Box>
      )}

      {/*  <Box
              sx={{
                width: "100%",
                height: "auto",
                background: "#f1f1f8" ,// '#fbfbfd'  '#f1f1f5'  "#F3F5F7" /
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px",
                gap: "24px",
                borderRadius: "8px",
                mt: "24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <Box>
                  <IconFileDXF width="20px" height="20px" fill="#000" />
                </Box>

                <Box>
                  <Typography variant="body2">File Example</Typography>
                  <Typography
                    variant="body2"
                    color="#777"
                    mt="4px"
                    fontSize={"14px"}
                  >
                    You can download the attached example and use it as a
                    starting point for you own file.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Button
                  sx={{
                    background: "#fff",
                    color: "#343745",
                    textTransform: "none",
                    boxShadow: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    ":hover": {
                      boxShadow: 4,
                    },
                  }}
                >
                  <IconDownload stroke="#000" width={"16px"} height={"16px"} />
                  Download
                </Button>
              </Box>
            </Box> */}
    </Box>
  );
};
